import React, { useState, useEffect } from 'react';
// Pada Paatha Processor (ppp.sarvaveda.org): the Sandhi screen's layout,
// calling POST v2/ppp. Pada-pāṭha in, pada-pāṭha out: compounds joined with
// their sandhi, इति and its repetition dropped, line layout kept. Rules are
// reviewed śākhā by śākhā; the server refuses plainly where they are not yet.
import { postRequest } from './Services/HttpClient';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import SanskritKeyboard from './SanskritKeyboard';
import './SandhiPage.css';

const STORAGE_KEY = 'ppp_last_input';

const PadaPaathaProcessor = () => {
  const [outputData, setOutputData] = useState('');
  const [inputData, setInputData] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return { inputWord: saved || '' };
  });
  const [vedshaakha, setVedshaakha] = useState('शाकल');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showKeyboard, setShowKeyboard] = useState(true);
  const [changedPadas, setChangedPadas] = useState(null);
  // Svara bio-markers U/A/S/P per syllable, and padas with an unwritten svarita
  const [bioMarkers, setBioMarkers] = useState(null);
  const [svaraFlags, setSvaraFlags] = useState([]);
  // Save input to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, inputData.inputWord);
  }, [inputData.inputWord]);

  const handleChange = async () => {
    if (!inputData.inputWord || !inputData.inputWord.trim()) return;

    setOutputData('');
    setChangedPadas(null);
    setBioMarkers(null);
    setSvaraFlags([]);
    setError(null);
    setLoading(true);

    try {
      const requestBody = { input: inputData.inputWord, vedshaakha };
      const result = await postRequest('v2/ppp', JSON.stringify(requestBody));
      setOutputData(result.data || '');
      if (!result.data && result.error) {
        // Server explains plainly, e.g. a śākhā whose rules are not reviewed yet
        setError(result.error);
      }
      setChangedPadas(result.changedPadas || null);
      setBioMarkers(result.data ? (result.bioMarkers || []) : null);
      setSvaraFlags(result.svaraFlags || []);
    } catch (err) {
      console.error('PPP API error:', err);
      setError('Pada Paatha processing failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyboardKeyPress = (key) => {
    if (key === 'backspace') {
      setInputData(prev => ({
        ...prev,
        inputWord: prev.inputWord.slice(0, -1)
      }));
    } else {
      setInputData(prev => ({
        ...prev,
        inputWord: prev.inputWord + key
      }));
    }
  };

  const handleKeyboardEnter = () => {
    setInputData(prev => ({
      ...prev,
      inputWord: prev.inputWord + '\n'
    }));
  };

  const handleKeyboardClose = () => {
    setShowKeyboard(false);
  };

  const clear = () => {
    setInputData({ inputWord: '' });
    setOutputData('');
    setChangedPadas(null);
    setBioMarkers(null);
    setSvaraFlags([]);
    setError(null);
  };

  const updateInputStateInputWord = (inputWordEvent) => {
    const inputWord = inputWordEvent.target.value;
    setInputData({ ...inputData, inputWord });
  };

  return (
    <Container fluid="md" className="sandhi-page">
      {/* Header with title */}
      <Row>
        <div className="sandhi-header">
          <h1 className="sandhi-title">पदपाठः (Pada Paatha Processor)</h1>
        </div>
      </Row>

      {/* Options Bar */}
      <Row className="sandhi-options-row">
        <div className="sandhi-options">
          <div className="sandhi-shakha-group">
            <label className="sandhi-label">
              <span className="label-text">वेदशाखा -</span>
              <select
                value={vedshaakha}
                onChange={(evt) => setVedshaakha(evt.target.value)}
                className="sandhi-select"
              >
                <option value="तैत्तिरीय">तैत्तिरीय</option>
                <option value="शाकल">शाकल</option>
                <option value="शौनक">शौनक</option>
                <option value="काण्व">काण्व</option>
                <option value="कौथुम">कौथुम</option>
                <option value="माध्यन्दिन">माध्यन्दिन</option>
              </select>
            </label>
          </div>
        </div>
      </Row>

      {/* Main Content - Side by side on tablet+, stacked on mobile */}
      <Row className="sandhi-main-row">
        {/* Input Column */}
        <Col xs={12} md={6} className="sandhi-col sandhi-input-col">
          <div className="sandhi-section">
            <div className="sandhi-section-header">
              <span className="section-label">निवेशः (Input)</span>
            </div>
            <textarea
              className="sandhi-textarea sandhi-input"
              onFocus={() => setShowKeyboard(true)}
              name="textarea"
              value={inputData.inputWord}
              disabled={loading}
              onChange={updateInputStateInputWord}
              placeholder="संस्कृत-पाठं लिखतु...&#10;(Enter pada-pāṭha text)"
            />
          </div>
        </Col>

        {/* Output Column */}
        <Col xs={12} md={6} className="sandhi-col sandhi-output-col">
          <div className="sandhi-section">
            <div className="sandhi-section-header">
              <span className="section-label">फलम्</span>
            </div>
            <textarea
              className="sandhi-textarea sandhi-output"
              name="textarea"
              value={loading ? 'विश्लेषणम्...' : outputData}
              readOnly
              placeholder="फलं अत्र प्रदर्श्यते...&#10;(Output will appear here)"
            />
          </div>
        </Col>
      </Row>

      {/* Action Buttons */}
      <Row className="sandhi-actions-row">
        <div className="sandhi-actions">
          <Button
            variant="primary"
            onClick={handleChange}
            disabled={loading || !inputData.inputWord || !inputData.inputWord.trim()}
            className="sandhi-submit-btn"
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                विश्लेषणम्...
              </>
            ) : (
              'Process'
            )}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={clear}
            className="sandhi-clear-btn"
          >
            स्वच्छं कुरु (Clear)
          </Button>
        </div>
      </Row>

      {/* Error Display */}
      {error && (
        <Row className="sandhi-error-row">
          <div className="sandhi-error">
            {error}
          </div>
        </Row>
      )}

      {/* Which padas changed, and by which rule */}
      {changedPadas && (
        <Row className="sandhi-error-row">
          <div className="sandhi-padapaatha-info" style={{ fontSize: '0.9em', opacity: 0.85 }}>
            {changedPadas.length} pada(s) changed
            {changedPadas.length > 0 && (
              <span>: {changedPadas.slice(0, 12).map(c => `${c.pada} → ${c.result}`).join(' · ')}
                {changedPadas.length > 12 ? ' …' : ''}</span>
            )}
          </div>
        </Row>
      )}

      {/* Svara bio-markers: U udātta, A anudātta, S svarita, P pracaya (VKG, 2026-11-01) */}
      {bioMarkers && (
        <Row className="sandhi-error-row">
          <div className="sandhi-padapaatha-info" style={{ fontSize: '0.9em', opacity: 0.85 }}>
            {bioMarkers.length === 0
              ? 'Bio-markers - none: the input carries no svara marks.'
              : <span>
                  Bio-markers (U udātta · A anudātta · S svarita · P pracaya) - {bioMarkers.length} pada(s) labelled. {bioMarkers.slice(0, 40).map(b => `${b.pada} ${b.labels}`).join(' · ')}
                  {bioMarkers.length > 40 ? ' …' : ''}
                </span>}
            {svaraFlags.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {svaraFlags.length} pada(s) leave the svarita after an udātta unwritten, as typed in the input: {svaraFlags.join(' · ')}
              </div>
            )}
          </div>
        </Row>
      )}

      {/* Sanskrit Keyboard */}
      <Row className="sandhi-keyboard-row">
        <SanskritKeyboard
          visible={showKeyboard}
          onKeyPress={handleKeyboardKeyPress}
          onEnter={handleKeyboardEnter}
          onClose={handleKeyboardClose}
        />
      </Row>
    </Container>
  );
};

export default PadaPaathaProcessor;
